import { memo, useEffect, useState } from "react";
import styled from "styled-components";
import { useAtom } from "jotai";
import { Button, Form, Input, DatePicker, message, Upload } from "antd";
import { UserOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { UI_ScreenStyles } from "../UI_ScreenStyles";
import { createBreadcrumbItems, commonBreadcrumbs } from "~/utils/breadcrumbUtils";
import { userInformationAtom } from "~/stores/UserStore";
import { ProfileAPIs } from "~/common/typescript/apis/ProfileAPIs";
import { User_Base, User_UpdateMyInfo_Request } from "~/common/typescript/models/User";
import { getHashColor } from "~/utils/hashStringUtil";

const ProfileScreen = () => {
  const [userInfo, setUserInfo] = useAtom(userInformationAtom);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    // Load user profile
    ProfileAPIs.remote_getMyProfile().then((profile) => {
      setUserInfo(profile);
      form.setFieldsValue({
        fullname: profile.fullname,
        email: profile.email,
        pronouns: profile.pronouns,
        job_title: profile.job_title,
        department: profile.department,
        aboutMe: profile.aboutMe,
        phoneNumber: profile.phoneNumber,
        birthday: profile.birthday_year
          ? dayjs(`${profile.birthday_year}-${profile.birthday_month || 1}-${profile.birthday_day || 1}`)
          : null,
      });
      // Set avatar URL if exists
      if (profile.avatar && typeof profile.avatar === 'object' && 'url' in profile.avatar) {
        setAvatarUrl((profile.avatar as any).url);
      }
    });
  }, [form, setUserInfo]);

  const handleUpdateProfile = async (values: any) => {
    setLoading(true);
    try {
      const formData = new FormData();

      if (values.fullname) formData.append('fullname', values.fullname);
      if (values.pronouns) formData.append('pronouns', values.pronouns);
      if (values.job_title) formData.append('job_title', values.job_title);
      if (values.department) formData.append('department', values.department);
      if (values.aboutMe) formData.append('aboutMe', values.aboutMe);
      if (values.phoneNumber) formData.append('phoneNumber', values.phoneNumber);

      if (values.birthday) {
        formData.append('birthday_year', values.birthday.year().toString());
        formData.append('birthday_month', (values.birthday.month() + 1).toString());
        formData.append('birthday_day', values.birthday.date().toString());
      }

      if (values.avatar && values.avatar.fileList && values.avatar.fileList[0]) {
        const file = values.avatar.fileList[0].originFileObj;
        if (file) {
          formData.append('avatar', file);
        }
      }

      await ProfileAPIs.remote_updateProfile(formData);
      message.success('Cập nhật thông tin thành công');

      // Reload profile
      const updatedProfile = await ProfileAPIs.remote_getMyProfile();
      setUserInfo(updatedProfile);
      if (updatedProfile.avatar && typeof updatedProfile.avatar === 'object' && 'url' in updatedProfile.avatar) {
        setAvatarUrl((updatedProfile.avatar as any).url);
      }
    } catch (error) {
      message.error('Cập nhật thông tin thất bại');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Get avatar color
  const getAvatarColor = () => {
    const username = userInfo?.username || 'user';
    const { backgroundColor, foregroundColor } = getHashColor(username);
    return { backgroundColor, foregroundColor };
  };

  const avatarColors = getAvatarColor();

  return (
    <UI_ScreenStyles.UI_Wrapper>
      <UI_ScreenStyles.UI_Breadcrumb
        items={createBreadcrumbItems([
          commonBreadcrumbs.home,
          { title: 'Thông tin cá nhân', icon: commonBreadcrumbs.settings.icon }
        ])}
      />

      <UI_ScreenStyles.UI_Content_Wrapper>
        <UI_Container>
          <UI_Header>
            <UI_AvatarSection>
              {avatarUrl ? (
                <UI_AvatarImage src={avatarUrl} alt="Avatar" />
              ) : (
                <UI_AvatarFallback style={{ backgroundColor: avatarColors.backgroundColor, color: avatarColors.foregroundColor }}>
                  {(userInfo?.fullname || userInfo?.username || 'U').slice(0, 2).toUpperCase()}
                </UI_AvatarFallback>
              )}
            </UI_AvatarSection>
            <UI_UserInfo>
              <UI_UserName>{userInfo?.fullname || userInfo?.username}</UI_UserName>
              <UI_UserEmail>{userInfo?.email}</UI_UserEmail>
            </UI_UserInfo>
          </UI_Header>

          <UI_FormSection>
            <UI_Title>Thông tin cá nhân</UI_Title>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleUpdateProfile}
            >
              <Form.Item name="avatar" label="Ảnh đại diện">
                <Upload
                  showUploadList={false}
                  beforeUpload={() => false}
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>Chọn ảnh</Button>
                </Upload>
              </Form.Item>

              <Form.Item name="fullname" label="Họ và tên">
                <Input placeholder="Nhập họ và tên" />
              </Form.Item>

              <Form.Item name="pronouns" label="Danh xưng">
                <Input placeholder="Ví dụ: Anh/Chị/Ông/Bà" />
              </Form.Item>

              <Form.Item name="email" label="Email">
                <Input disabled />
              </Form.Item>

              <Form.Item name="phoneNumber" label="Số điện thoại">
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>

              <Form.Item name="job_title" label="Chức vụ">
                <Input placeholder="Nhập chức vụ" />
              </Form.Item>

              <Form.Item name="department" label="Phòng ban">
                <Input placeholder="Nhập phòng ban" />
              </Form.Item>

              <Form.Item name="birthday" label="Ngày sinh">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Chọn ngày sinh" />
              </Form.Item>

              <Form.Item name="aboutMe" label="Giới thiệu bản thân">
                <Input.TextArea rows={3} placeholder="Giới thiệu về bản thân" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Lưu thay đổi
                </Button>
              </Form.Item>
            </Form>
          </UI_FormSection>

          <UI_FormSection>
            <UI_Title>Đổi mật khẩu</UI_Title>
            <UI_PasswordNote>
              Để đổi mật khẩu, vui lòng liên hệ quản trị viên hoặc sử dụng chức năng "Quên mật khẩu" trên trang đăng nhập.
            </UI_PasswordNote>
          </UI_FormSection>
        </UI_Container>
      </UI_ScreenStyles.UI_Content_Wrapper>
    </UI_ScreenStyles.UI_Wrapper>
  );
};

export default memo(ProfileScreen);

const UI_Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const UI_Header = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 24px;
`;

const UI_AvatarSection = styled.div`
  flex-shrink: 0;
`;

const UI_AvatarImage = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e0e0e0;
`;

const UI_AvatarFallback = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 600;
`;

const UI_UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UI_UserName = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const UI_UserEmail = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
`;

const UI_FormSection = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`;

const UI_Title = styled.h3`
  margin: 0 0 24px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e8e8e8;
  padding-bottom: 12px;
`;

const UI_PasswordNote = styled.p`
  color: #666;
  font-size: 14px;
  line-height: 1.6;
`;
